// https://boost.allnetru.pro/api/dev/dicts/routes?prefix=api/partner/creatives-desk
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
import {
  ChangeCreativesDeskStatusParams,
  CreativesDeskItemResponse,
  GetCreativesDeskArtifactResponse,
  GetCreativesDeskEventsResponse,
  GetCreativesDeskIdeasResponse,
  GetCreativesDeskPlanResponse,
  GetCreativesDeskResponse,
  HoldCreativesDeskItemParams,
  LaunchCreativesDeskParams,
  OrderCreativesDeskIdeaParams,
  RenameCreativesDeskItemParams,
  ReplyCreativesDeskModerationParams,
  SetCreativesDeskBudgetParams,
  SetCreativesDeskItemBudgetParams,
  CreativesDeskProActionResponse,
  GetCreativesDeskProActionsResponse,
  GetCreativesDeskWordsResponse,
  SaveCreativesDeskWordsParams,
  SwitchCreativesDeskIntroStyleParams,
  SetCreativesDeskCtaParams,
  WatchCreativesDeskItemParams,
  CreateCreativeHelpParams,
  CreativeHelpRequest,
  GetCreativeHelpResponse,
} from './models';

const partner_creatives_desk = `partner_creatives_desk`;

export const partnerCreativesDeskApi = createApi({
  reducerPath: partner_creatives_desk,
  baseQuery: baseQueryWithReauth,
  tagTypes: [partner_creatives_desk],
  endpoints: (build) => ({
    getCreativesDesk: build.query<GetCreativesDeskResponse, void>({
      query: () => ({
        url: '/partner/creatives-desk',
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: partner_creatives_desk, id: 'LIST' }],
    }),
    // creative help: the scheduler and the request still with our creative team
    getCreativeHelp: build.query<GetCreativeHelpResponse, void>({
      query: () => ({
        url: '/partner/creatives-desk/creative-help',
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: partner_creatives_desk, id: 'CREATIVE_HELP' }],
    }),
    createCreativeHelp: build.mutation<{ data: CreativeHelpRequest }, CreateCreativeHelpParams>({
      query: (body) => ({
        url: '/partner/creatives-desk/creative-help',
        method: 'POST',
        body,
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [{ type: partner_creatives_desk, id: 'CREATIVE_HELP' }],
    }),
    getCreativesDeskEvents: build.query<GetCreativesDeskEventsResponse, void>({
      query: () => ({
        url: '/partner/creatives-desk/events',
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: partner_creatives_desk, id: 'EVENTS' }],
    }),
    // eventId scopes the band to the desk's header selection (exactly one
    // event picked); without it the backend self-scopes to the newest
    // approved creative's event
    getCreativesDeskPlan: build.query<
      GetCreativesDeskPlanResponse,
      { eventId?: number; orderBudget?: number } | void
    >({
      query: (arg) => ({
        url: '/partner/creatives-desk/plan',
        params: {
          ...(arg?.eventId ? { event_id: arg.eventId } : {}),
          // the order window prices a budget of the sponsor's choosing
          ...(arg?.orderBudget !== undefined ? { order_budget: arg.orderBudget } : {}),
        },
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: partner_creatives_desk, id: 'PLAN' }],
    }),
    // artifactId/updatedAt are not part of the URL — they key the cache: a
    // redraft recomposes the same artifact in place (the timestamp moves),
    // and the changed arg is what makes the card fetch the fresh render
    // instead of the cached one
    getCreativesDeskArtifact: build.query<
      GetCreativesDeskArtifactResponse,
      { creativeId: number; artifactId: number; updatedAt?: string | null }
    >({
      query: ({ creativeId }) => ({
        url: `/partner/creatives-desk/${creativeId}/artifact`,
        headers: { Accept: 'application/json' },
      }),
    }),
    // the post "Create with your own image" opens on: the event's ad chrome
    // around an empty square — rendered by the backend, nothing saved
    getCreativesDeskOwnImageDraft: build.query<
      GetCreativesDeskArtifactResponse,
      { eventId: number }
    >({
      query: ({ eventId }) => ({
        url: '/partner/creatives-desk/own-image/draft',
        params: { event_id: eventId },
        headers: { Accept: 'application/json' },
      }),
    }),
    changeCreativesDeskStatus: build.mutation<
      CreativesDeskItemResponse,
      ChangeCreativesDeskStatusParams
    >({
      query: ({ creativeId, status, ...body }) => ({
        url: `/partner/creatives-desk/${creativeId}/status/${status}`,
        method: 'PATCH',
        headers: { Accept: 'application/json' },
        body,
      }),
      invalidatesTags: [
        { type: partner_creatives_desk, id: 'LIST' },
        { type: partner_creatives_desk, id: 'PLAN' },
        // an archive brings the idea back to the gallery, a launch or a
        // set-aside moves its slot counts
        { type: partner_creatives_desk, id: 'IDEAS' },
      ],
    }),
    launchCreativesDesk: build.mutation<GetCreativesDeskPlanResponse, LaunchCreativesDeskParams>({
      query: (body) => ({
        url: '/partner/creatives-desk/launch',
        method: 'POST',
        body,
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [
        { type: partner_creatives_desk, id: 'LIST' },
        { type: partner_creatives_desk, id: 'PLAN' },
        // an archive brings the idea back to the gallery, a launch or a
        // set-aside moves its slot counts
        { type: partner_creatives_desk, id: 'IDEAS' },
      ],
    }),
    // every approved creative of the event takes the budget, their campaign
    // groups follow — the rows AND the plan change
    setCreativesDeskBudget: build.mutation<
      GetCreativesDeskPlanResponse,
      SetCreativesDeskBudgetParams
    >({
      query: (body) => ({
        url: '/partner/creatives-desk/budget',
        method: 'PATCH',
        body,
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [
        { type: partner_creatives_desk, id: 'LIST' },
        { type: partner_creatives_desk, id: 'PLAN' },
      ],
    }),
    // one creative's own budget — its campaign group follows, the plan re-sums
    setCreativesDeskItemBudget: build.mutation<
      CreativesDeskItemResponse,
      SetCreativesDeskItemBudgetParams
    >({
      query: ({ creativeId, budget }) => ({
        url: `/partner/creatives-desk/${creativeId}/budget`,
        method: 'PATCH',
        headers: { Accept: 'application/json' },
        body: { budget },
      }),
      invalidatesTags: [
        { type: partner_creatives_desk, id: 'LIST' },
        { type: partner_creatives_desk, id: 'PLAN' },
      ],
    }),
    // set aside / put back: the row's flag flips and its group pauses or
    // resumes — the plan's totals and launch gate change with it
    // "Edit the words yourself": the creative's lines, read off its current version
    getCreativesDeskWords: build.query<GetCreativesDeskWordsResponse, { creativeId: number }>({
      query: ({ creativeId }) => ({
        url: `/partner/creatives-desk/${creativeId}/words`,
        headers: { Accept: 'application/json' },
      }),
      providesTags: (_result, _error, { creativeId }) => [
        { type: partner_creatives_desk, id: `WORDS-${creativeId}` },
      ],
      keepUnusedDataFor: 0,
    }),
    // free and agent-less: the words become the creative's new version
    saveCreativesDeskWords: build.mutation<CreativesDeskItemResponse, SaveCreativesDeskWordsParams>(
      {
        query: ({ creativeId, words }) => ({
          url: `/partner/creatives-desk/${creativeId}/words`,
          method: 'PATCH',
          headers: { Accept: 'application/json' },
          body: { words },
        }),
        // the saved lines are the creative's now — the panel re-reads them
        invalidatesTags: (_result, _error, { creativeId }) => [
          { type: partner_creatives_desk, id: 'LIST' },
          { type: partner_creatives_desk, id: `WORDS-${creativeId}` },
        ],
      },
    ),
    // "Intro style" on the creative: free, in place, no new version
    switchCreativesDeskIntroStyle: build.mutation<
      CreativesDeskItemResponse,
      SwitchCreativesDeskIntroStyleParams
    >({
      query: ({ creativeId, intro_style_id }) => ({
        url: `/partner/creatives-desk/${creativeId}/intro-style`,
        method: 'PATCH',
        headers: { Accept: 'application/json' },
        body: { intro_style_id },
      }),
      invalidatesTags: [{ type: partner_creatives_desk, id: 'LIST' }],
    }),
    // the CTA button the ad closes on: free, in place
    setCreativesDeskCta: build.mutation<CreativesDeskItemResponse, SetCreativesDeskCtaParams>({
      query: ({ creativeId, cta }) => ({
        url: `/partner/creatives-desk/${creativeId}/cta`,
        method: 'PATCH',
        headers: { Accept: 'application/json' },
        body: { cta },
      }),
      invalidatesTags: [{ type: partner_creatives_desk, id: 'LIST' }],
    }),
    // "Try again" on an order the platform could not make — charged anew
    retryCreativesDeskItem: build.mutation<CreativesDeskItemResponse, { creativeId: number }>({
      query: ({ creativeId }) => ({
        url: `/partner/creatives-desk/${creativeId}/retry`,
        method: 'POST',
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [
        { type: partner_creatives_desk, id: 'LIST' },
        { type: partner_creatives_desk, id: 'PLAN' },
      ],
    }),
    holdCreativesDeskItem: build.mutation<CreativesDeskItemResponse, HoldCreativesDeskItemParams>({
      query: ({ creativeId, is_held }) => ({
        url: `/partner/creatives-desk/${creativeId}/hold`,
        method: 'PATCH',
        headers: { Accept: 'application/json' },
        body: { is_held },
      }),
      invalidatesTags: [
        { type: partner_creatives_desk, id: 'LIST' },
        { type: partner_creatives_desk, id: 'PLAN' },
        // a set-aside creative gives its idea's slot back
        { type: partner_creatives_desk, id: 'IDEAS' },
      ],
    }),
    // the sponsor's own name for a creative, or the default put back (null)
    renameCreativesDeskItem: build.mutation<
      CreativesDeskItemResponse,
      RenameCreativesDeskItemParams
    >({
      query: ({ creativeId, name }) => ({
        url: `/partner/creatives-desk/${creativeId}/name`,
        method: 'PATCH',
        headers: { Accept: 'application/json' },
        body: { name },
      }),
      invalidatesTags: [
        { type: partner_creatives_desk, id: 'LIST' },
        { type: partner_creatives_desk, id: 'PLAN' },
      ],
    }),
    // "Tell me if a slot frees up" on a slot-taken creative, or stop watching
    watchCreativesDeskItem: build.mutation<CreativesDeskItemResponse, WatchCreativesDeskItemParams>(
      {
        query: ({ creativeId, is_watching }) => ({
          url: `/partner/creatives-desk/${creativeId}/watch`,
          method: 'PATCH',
          headers: { Accept: 'application/json' },
          body: { is_watching },
        }),
        invalidatesTags: [{ type: partner_creatives_desk, id: 'LIST' }],
      },
    ),
    replyCreativesDeskModeration: build.mutation<
      CreativesDeskItemResponse,
      ReplyCreativesDeskModerationParams
    >({
      query: ({ creativeId, comment }) => ({
        url: `/partner/creatives-desk/${creativeId}/moderation-reply`,
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: { comment },
      }),
      invalidatesTags: [
        { type: partner_creatives_desk, id: 'LIST' },
        { type: partner_creatives_desk, id: 'PLAN' },
      ],
    }),
    // Boost PRO's log of moves on the launched campaign — one event's or all
    getCreativesDeskProActions: build.query<
      GetCreativesDeskProActionsResponse,
      { eventId?: number } | void
    >({
      query: (params) => ({
        url: '/partner/creatives-desk/pro-actions',
        params: params && params.eventId ? { event_id: params.eventId } : undefined,
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: partner_creatives_desk, id: 'PRO_ACTIONS' }],
    }),
    agreeCreativesDeskProAction: build.mutation<
      CreativesDeskProActionResponse,
      { actionId: number }
    >({
      query: ({ actionId }) => ({
        url: `/partner/creatives-desk/pro-actions/${actionId}/agree`,
        method: 'PATCH',
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [{ type: partner_creatives_desk, id: 'PRO_ACTIONS' }],
    }),
    // the reversal moves money and pauses or resumes a group — the plan and the rows follow
    disagreeCreativesDeskProAction: build.mutation<
      CreativesDeskProActionResponse,
      { actionId: number; force?: boolean }
    >({
      query: ({ actionId, force }) => ({
        url: `/partner/creatives-desk/pro-actions/${actionId}/disagree`,
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: force ? { force: true } : {},
      }),
      invalidatesTags: [
        { type: partner_creatives_desk, id: 'PRO_ACTIONS' },
        { type: partner_creatives_desk, id: 'LIST' },
        { type: partner_creatives_desk, id: 'PLAN' },
      ],
    }),
    getCreativesDeskIdeas: build.query<GetCreativesDeskIdeasResponse, { eventId: number }>({
      query: ({ eventId }) => ({
        url: '/partner/creatives-desk/ideas',
        params: { event_id: eventId },
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: partner_creatives_desk, id: 'IDEAS' }],
    }),
    // artifactId is not part of the URL — it keys the cache: relinking an
    // idea to another library entry changes the id in the ideas list, and
    // the changed arg is what refetches the fresh render (the same pattern
    // getCreativesDeskArtifact uses for redrafts)
    getCreativesDeskIdeaArtifact: build.query<
      GetCreativesDeskArtifactResponse,
      { ideaId: number; artifactId: number; eventId: number }
    >({
      // the preview is framed as a post of the event the gallery is for
      query: ({ ideaId, eventId }) => ({
        url: `/partner/creatives-desk/ideas/${ideaId}/artifact`,
        params: { event_id: eventId },
        headers: { Accept: 'application/json' },
      }),
    }),
    orderCreativesDeskIdea: build.mutation<CreativesDeskItemResponse, OrderCreativesDeskIdeaParams>(
      {
        query: ({
          ideaId,
          event_id,
          intro_style_id,
          featured_brief_item_id,
          product_visual_file_id,
          switch_from_id,
        }) => ({
          url: `/partner/creatives-desk/ideas/${ideaId}/order`,
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: {
            event_id,
            ...(intro_style_id ? { intro_style_id } : {}),
            ...(featured_brief_item_id ? { featured_brief_item_id } : {}),
            ...(product_visual_file_id ? { product_visual_file_id } : {}),
            ...(switch_from_id ? { switch_from_id } : {}),
          },
        }),
        // an order changes the desk (a pending row appears), the balance on
        // the plan, and the gallery's slot occupancy
        invalidatesTags: [
          { type: partner_creatives_desk, id: 'LIST' },
          { type: partner_creatives_desk, id: 'PLAN' },
          { type: partner_creatives_desk, id: 'IDEAS' },
        ],
      },
    ),
    // "Create with your own image": the picture (cut square — crop in its
    // pixels), the post and the event, as multipart
    createCreativesDeskOwnImage: build.mutation<CreativesDeskItemResponse, FormData>({
      query: (body) => ({
        url: '/partner/creatives-desk/own-image',
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      }),
      invalidatesTags: [
        { type: partner_creatives_desk, id: 'LIST' },
        { type: partner_creatives_desk, id: 'PLAN' },
      ],
    }),
    // another picture for the sponsor's own creative — its words kept
    replaceCreativesDeskOwnImage: build.mutation<
      CreativesDeskItemResponse,
      { id: number; body: FormData }
    >({
      query: ({ id, body }) => ({
        url: `/partner/creatives-desk/${id}/own-image`,
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      }),
      invalidatesTags: [{ type: partner_creatives_desk, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetCreativesDeskQuery,
  useGetCreativesDeskEventsQuery,
  useGetCreativesDeskPlanQuery,
  useGetCreativesDeskArtifactQuery,
  useChangeCreativesDeskStatusMutation,
  useGetCreativesDeskProActionsQuery,
  useAgreeCreativesDeskProActionMutation,
  useDisagreeCreativesDeskProActionMutation,
  useGetCreativesDeskIdeasQuery,
  useGetCreativesDeskIdeaArtifactQuery,
  useOrderCreativesDeskIdeaMutation,
  useCreateCreativesDeskOwnImageMutation,
  useGetCreativesDeskOwnImageDraftQuery,
  useReplaceCreativesDeskOwnImageMutation,
  useLaunchCreativesDeskMutation,
  useSetCreativesDeskBudgetMutation,
  useSetCreativesDeskItemBudgetMutation,
  useHoldCreativesDeskItemMutation,
  useRenameCreativesDeskItemMutation,
  useWatchCreativesDeskItemMutation,
  useRetryCreativesDeskItemMutation,
  useGetCreativesDeskWordsQuery,
  useSaveCreativesDeskWordsMutation,
  useSwitchCreativesDeskIntroStyleMutation,
  useSetCreativesDeskCtaMutation,
  useReplyCreativesDeskModerationMutation,
  useGetCreativeHelpQuery,
  useCreateCreativeHelpMutation,
} = partnerCreativesDeskApi;
